import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Server, Cloud, Shield, Zap, BarChart, Clock, Check, ChevronDown, Mail, Phone, MapPin, Menu, X } from 'lucide-react';
import { mockData } from '../utils/mock';

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for contacting us! We will get back to you soon.');
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Nix Southeast Asia</h1>
                <p className="text-xs text-slate-600">Data Center Solutions</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('advantages')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Advantages</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Testimonials</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-cyan-600 hover:bg-cyan-700 text-white">Contact Us</Button>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-slate-700 hover:text-cyan-600 transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-slate-700 hover:text-cyan-600 transition-colors">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-slate-700 hover:text-cyan-600 transition-colors">Services</button>
              <button onClick={() => scrollToSection('advantages')} className="block w-full text-left py-2 text-slate-700 hover:text-cyan-600 transition-colors">Advantages</button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left py-2 text-slate-700 hover:text-cyan-600 transition-colors">Testimonials</button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">Contact Us</Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1580106815433-a5b1d1d53d85" 
            alt="Data Center" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 to-cyan-900/60"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center pt-16 pb-12">
            <a 
              href="http://nix.global/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mb-6 px-4 py-2 bg-cyan-500/20 backdrop-blur-sm rounded-full border border-cyan-400/30 hover:bg-cyan-500/30 hover:border-cyan-400/50 transition-all duration-300 cursor-pointer"
            >
              <span className="text-cyan-300 font-semibold text-sm">Powered by Nix Global</span>
            </a>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Enterprise Data Center
              <span className="block text-cyan-400">Solutions for Southeast Asia</span>
            </h2>
            <p className="text-xl text-slate-200 mb-10 leading-relaxed">
              Delivering world-class infrastructure, cloud hosting, and managed services with unparalleled reliability and performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => scrollToSection('services')} size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg">
                Explore Services
              </Button>
              <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg">
                Get Started
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mt-16">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="text-4xl font-bold text-cyan-400 mb-2">{stat.value}</div>
                <div className="text-slate-200 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">About Nix Southeast Asia</h2>
              <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                A trusted subsidiary of Nix Global, bringing decades of expertise to the Southeast Asian market
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1762163516269-3c143e04175c" 
                  alt="Our Infrastructure" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Our Story</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {mockData.about.history}
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Our Vision</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {mockData.about.vision}
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Our Mission</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {mockData.about.mission}
                  </p>
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className="mt-16 grid md:grid-cols-3 gap-8">
              {mockData.about.achievements.map((achievement, index) => (
                <Card key={index} className="border-2 hover:border-cyan-500 transition-all hover:shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-cyan-600">{achievement.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">{achievement.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive data center solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-cyan-500 hover:-translate-y-2">
                  <CardHeader>
                    <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-600 transition-colors">
                      <IconComponent className="w-7 h-7 text-cyan-600 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-slate-600">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section id="advantages" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Nix Southeast Asia</h2>
              <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Experience the difference with our industry-leading solutions
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {mockData.advantages.map((advantage, index) => (
                <div key={index} className="flex gap-4 p-6 rounded-xl bg-slate-50 hover:bg-cyan-50 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{advantage.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{advantage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1698668975271-2ba9a323be6b" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Client Success Stories</h2>
            <div className="w-20 h-1 bg-cyan-400 mx-auto mb-6"></div>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Trusted by leading organizations across Southeast Asia
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{testimonial.name}</CardTitle>
                      <p className="text-cyan-400 text-sm">{testimonial.position}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-200 leading-relaxed mb-4">"{testimonial.feedback}"</p>
                  <p className="text-slate-400 text-sm font-semibold">{testimonial.company}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Get In Touch</h2>
              <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Let's discuss how we can power your digital infrastructure
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                      <Input
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                      <Input
                        type="text"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                      <Textarea
                        placeholder="Tell us about your requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={5}
                        className="w-full"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg">
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <div className="space-y-8">
                <Card className="border-2 hover:border-cyan-500 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Email Us</CardTitle>
                        <CardDescription className="text-base">{mockData.contact.email}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 hover:border-cyan-500 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                        <Phone className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Call Us</CardTitle>
                        <CardDescription className="text-base">{mockData.contact.phone}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 hover:border-cyan-500 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Visit Us</CardTitle>
                        <CardDescription className="text-base leading-relaxed">{mockData.contact.address}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 bg-gradient-to-br from-cyan-50 to-blue-50">
                  <CardHeader>
                    <CardTitle className="text-lg">Business Hours</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-slate-700">
                      <p className="font-semibold">{mockData.contact.hours.weekdays}</p>
                      <p>{mockData.contact.hours.weekend}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Server className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Nix Southeast Asia</h3>
                </div>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Leading data center solutions provider in Southeast Asia, backed by Nix Global's expertise.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-slate-400 hover:text-cyan-400 transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-slate-400 hover:text-cyan-400 transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('advantages')} className="text-slate-400 hover:text-cyan-400 transition-colors">Advantages</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="text-slate-400 hover:text-cyan-400 transition-colors">Testimonials</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li className="text-slate-400">Cloud Hosting</li>
                <li className="text-slate-400">Server Management</li>
                <li className="text-slate-400">Colocation Services</li>
                <li className="text-slate-400">Disaster Recovery</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="text-slate-400 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>{mockData.contact.address}</span>
                </li>
                <li className="text-slate-400 flex items-center gap-2">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{mockData.contact.phone}</span>
                </li>
                <li className="text-slate-400 flex items-center gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>{mockData.contact.email}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400">
              © {new Date().getFullYear()} Nix Southeast Asia. A subsidiary of Nix Global. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;